<template>
  <div class="google">
    <Breadcrumb
      :items="breadcrumbItems"
      class="google__breadcrumb"
    />
    <div class="google__section--no-padding">
      <GoogleStage
        :data="stageData"
        @go-to-section="goToSection"
      />
    </div>
    <div class="google__section">
      <VideoPlayer
        ref="videoPlayer"
        :src="videoData.src"
        :poster="videoData.poster"
        class="google-intro__video"
        muted
      />
    </div>
    <div class="google__section">
      <GoogleInfoTiles
        :tiles-data="tilesData[0]"
        :show-link="true"
        :title="tilesData[0].title"
        :subtitle="tilesData[0].subtitle"
        @go-to-section="goToSection"
      />
    </div>
    <div
      v-if="teaser.first && teaser.first.offer"
      class="google__section"
    >
      <div
        ref="pixelBuds"
        class="google__section__anchor"
      ></div>
      <ProductOfferTileXlContainer
        :ambient-image="teaser.first.ambientImage"
        :description="teaser.first.copy"
        :offer="teaser.first.offer"
        :product-image="teaser.first.productImage"
        :title="teaser.first.title"
        class="google__teaser-xl google__mb"
        show-local-image
        show-local-title
        lazy
      />
    </div>
    <div
      v-if="teaser.table.length && offers[1]?.offer"
      class="google__section"
    >
      <div
        ref="specsTable"
        class="google__section__anchor"
      ></div>
      <GoogleTable
        :header="teaser.table[0].title"
        :table-data="specsData"
        :offers="teaser.table"
        :offer="offers[1].offer"
      />
    </div>
    <div
      v-if="teaser.second && teaser.second.offer"
      class="google__section"
    >
      <div
        ref="pixelStand"
        class="google__section__anchor"
      ></div>

      <div class="google__section__header">
        <Heading
          :text="teaser.second.headerTitle"
          class="google-stage__grid__item"
          font-weight="ultra"
          size="lg"
          tag="h2"
          text-uppercase
          align="center"
          spaces-top
          spaces-bottom
        />
      </div>
      <ProductOfferTileXlContainer
        :ambient-image="teaser.second.ambientImage"
        :copy="teaser.second.copy"
        :offer="teaser.second.offer"
        :product-image="teaser.second.productImage"
        :title="teaser.second.title"
        heading-tag="h3"
        class="google__teaser-xl google__mb"
        show-local-image
        show-local-title
        alignment="right"
        lazy
      />
    </div>
    <div class="google__section">
      <GoogleInfoTiles :tiles-data="tilesData[1]" />
    </div>
    <div
      v-if="teaser.third.length"
      class="google__section google__section--no-padding"
    >
      <div
        ref="pixelCases"
        class="google__section__anchor"
      ></div>
      <div class="google__section__header">
        <Heading
          :text="teaser.third[0].mainTitle"
          class="google-stage__grid__item"
          font-weight="ultra"
          size="lg"
          tag="h2"
          text-uppercase
          align="center"
          spaces-top
        />
        <Heading
          :text="teaser.third[0].subtitle"
          class="google-stage__grid__item"
          font-weight="regular"
          size="sm"
          tag="h3"
          align="center"
          spaces-top
          spaces-bottom
        />
      </div>
      <ProductOfferTileSMContainer
        :items="teaser.third"
        show-available-variant
        class="google__mb"
        tile-type="medium"
        heading-tag="h4"
      />
    </div>
    <div class="google__section">
      <GoogleInfoTiles :tiles-data="tilesData[2]" />
    </div>
    <div class="google__section">
      <GoogleTeasers :teaser-items="bannerData" />
    </div>
    <div class="google__section">
      <GoogleFaq :faqs="faqs" />
    </div>
  </div>
</template>

<script>
import { ref } from 'vue';
import {
  PRODUCT_DATA,
  SPECS_DATA,
  STAGE_DATA,
  TILES_DATA,
  BANNER_DATA,
  TEASER_DATA,
  VIDEO_DATA,
  FAQS_DATA,
} from './product-data';
import { Heading, VideoPlayer } from '@i22-td-smarthome/component-library';
import Breadcrumb from '@/components/breadcrumb.vue';
import ProductOfferTileXlContainer from '@/components/product-offer-tile-xl-container.vue';
import ProductOfferTileSMContainer from '@/components/product-offer-tile-s-m-container.vue';
import { getOffersByGroup, loadOffersForPromotions } from '@/lib/offers-by-group';
import GoogleStage from '@/components/google/google-stage.vue';
import GoogleInfoTiles from '@/components/google/google-info-tiles.vue';
import GoogleTeasers from '@/components/google/google-teasers.vue';
import GoogleTable from '@/components/google/google-table.vue';
import GoogleFaq from '@/components/google/google-faq.vue';
import { Track } from '~/helpers/page-tracking';
import { getHeader } from '~/helpers/page-header-compositon';

export default {
  name: 'GoogleWorld',
  components: {
    Breadcrumb,
    Heading,
    VideoPlayer,
    ProductOfferTileXlContainer,
    ProductOfferTileSMContainer,
    GoogleStage,
    GoogleInfoTiles,
    GoogleTeasers,
    GoogleTable,
    GoogleFaq,
  },
  setup: () => {
    Track({ page_type: 'theme.landing' });

    getHeader({
      title: ref('Google Pixel Buds entdecken'),
      description: ref(
        'Die Premium In-Ear-Kopfhörer mit aktivem Noise Cancelling für vollen Klang. Jetzt mehr über die Google Pixel Buds erfahren & passendes Zubehör entdecken!'
      ),
    });
  },
  async asyncData() {
    const offers = await loadOffersForPromotions(PRODUCT_DATA);
    return {
      offers,
      specsData: SPECS_DATA,
      stageData: STAGE_DATA,
      tilesData: TILES_DATA,
      bannerData: BANNER_DATA,
      teaserData: TEASER_DATA,
      videoData: VIDEO_DATA,
      faqs: FAQS_DATA,
    };
  },
  data() {
    return {
      breadcrumbItems: [
        {
          label: 'Google',
          to: 'google',
        },
      ],
    };
  },
  computed: {
    teaser() {
      return {
        first: this.getOffersByGroup(this.offers, 'first')[0],
        second: this.getOffersByGroup(this.offers, 'second')[0],
        third: this.getOffersByGroup(this.offers, 'third'),
        fourth: this.getOffersByGroup(this.offers, 'fourth'),
        table: this.getOffersByGroup(this.offers, 'table'),
      };
    },
  },
  methods: {
    getOffersByGroup,
    goToSection(refString) {
      this.$nextTick(() => {
        if (!this.$refs[refString]) return;
        this.$refs[refString].scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      });
    },
  },
  jsonld() {
    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: this.faqs.map((item) => ({
        '@type': 'Question',
        name: item.title,
        acceptedAnswer: {
          '@type': 'Answer',
          text: item.content,
        },
      })),
    };
  },
};
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.google {
  &__section {
    position: relative;
    margin-bottom: 6rem;

    &--no-padding {
      margin-bottom: 0;
    }

    &__anchor {
      position: absolute;
      left: 0;
      top: -100px;
    }

    &__header {
      margin: 2rem 0;
    }
  }

  &__teaser {
    &-xl {
      :deep(.product-offer-detail-link__link) {
        img {
          width: 100%;
          aspect-ratio: 16 / 11;
        }
      }

      :deep(.product-offer-tile-xl__ambient-image) {
        img {
          width: 100%;
          aspect-ratio: 20 / 11;
        }
      }
    }
  }
}

:deep(.no-data) {
  .prices__main,
  .product-offer-color-select,
  .product-offer-tile-xl__content-link {
    display: none;
  }

  .product-offer-tile-xl__content-middle {
    justify-content: flex-start;
  }
}
</style>
